// Constants
import { FileTypeExtensions } from "@ms/uno-constants/lib/local/FileTypeExtensions";
import { FileTypesWithPreview, NonPreviewableSupportedFileTypes, SupportedReferenceTypes } from "@ms/uno-constants/lib/local/FileTypeConstants";
// Utilities
import indexOf from "lodash/indexOf";
/**
 * Utility functions for handling content extensions and file types
 */ /**
 * Extracts the file extension from a URL and filename alias.
 * Prioritizes extensions from known file types first, then parses the alias,
 * and finally checks for specific types like images.
 *
 * @param url - The URL of the attachment
 * @param alias - The display name or alias of the attachment
 * @returns The file extension or null if none could be determined
 */ export function getFileExtension(url, alias) {
    // Step 1: Check if URL matches a known type extension
    // Loop through all supported reference types
    for (const referenceType of Object.values(SupportedReferenceTypes)){
        // Skip 'Other' and 'TeamsHostedApp' types as they don't have specific extensions
        if (referenceType === SupportedReferenceTypes.Other || referenceType === SupportedReferenceTypes.TeamsHostedApp) {
            continue;
        }
        // Check for matching extensions for this reference type
        const extensions = FileTypeExtensions[referenceType];
        if (!extensions) {
            continue;
        }
        // Look for any extension from this type in the URL
        for (const extension of extensions){
            if (url.toLowerCase().includes("." + extension.toLowerCase())) {
                return extension.toLowerCase();
            }
        }
    }
    // Step 2: Fall back to parsing the alias
    const aliasParts = alias.split(".");
    if (aliasParts.length > 1) {
        const possibleExtension = aliasParts[aliasParts.length - 1];
        // Simple check for common extension lengths (3-4 characters)
        if (possibleExtension.length === 3 || possibleExtension.length === 4) {
            return possibleExtension.toLowerCase();
        }
    }
    // Step 3: Check for specific types (images, etc.)
    // Extract the part after the last dot and before any URL parameters
    const regexed = url.slice((url.lastIndexOf(".") - 1 >>> 0) + 2).split(/\#|\?|\&|\s/g);
    if (regexed.length > 0 && indexOf(FileTypesWithPreview, regexed[0].toLowerCase()) >= 0) {
        return regexed[0].toLowerCase();
    }
    return null;
}
/**
 * Maps a file extension to its corresponding {@link SupportedReferenceTypes}, if any.
 *
 * @param extension - The file extension (case-insensitive), e.g. "one" or "docx"
 * @returns The matching reference type, or null if the extension is not recognized
 */ export function getReferenceTypeFromExtension(extension) {
    const normalizedExtension = extension.toLowerCase();
    for (const referenceType of Object.keys(FileTypeExtensions)){
        const extensions = FileTypeExtensions[referenceType];
        if (extensions?.some((ext)=>ext.toLowerCase() === normalizedExtension)) {
            return referenceType;
        }
    }
    return null;
}
/**
 * Determines whether a file extension corresponds to a non-previewable file type
 * (e.g. OneNote, Project, Visio, Form). Non-previewable types must be opened via their
 * raw URL rather than the Teams file preview, since preview drops URL targeting parameters.
 *
 * @param extension - The file extension (case-insensitive), e.g. "one"
 * @returns True if the extension maps to a non-previewable reference type
 */ export function isNonPreviewableFileType(extension) {
    const referenceType = getReferenceTypeFromExtension(extension);
    return referenceType != null && NonPreviewableSupportedFileTypes.includes(referenceType);
}
